import { useState, useEffect } from "react";
import {
  Nav,
  NavbarToggler,
  Container,
  Collapse,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  NavItem,
  NavLink
} from "reactstrap";
import { Link, useLocation } from "react-router-dom";
import LanguageButton from "./components/LanguageButton";
import { useTranslation } from "react-i18next";

export const Navbar = ({ fixedTop = true }) => {
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const toggle = () => setIsOpenMenu(!isOpenMenu);

  const isActive = (path) => (pathname === path ? "active" : "");

  // Efecto para cambiar el estilo del navbar al hacer scroll
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Efecto para manejar scroll después de navegación con hash
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.substring(1);
      setTimeout(() => {
        if (id === "footer") {
          const footer =
            document.querySelector("footer") ||
            document.querySelector(".footer-section");
          if (footer) footer.scrollIntoView({ behavior: "smooth" });
        } else {
          const element = document.getElementById(id);
          if (element) element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, []);

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
      setIsOpenMenu(false);
    }
  };

  return (
    <>
      <nav
        className={`navbar navbar-expand-lg navbar-custom sticky sticky-dark nav-sticky ${
          fixedTop ? "fixed-top" : ""
        } ${scrolled ? "navbar-scrolled" : ""}`}
        id="navbar"
        style={{
          backgroundColor: scrolled
            ? "rgba(0, 0, 0, 0.95)"
            : "rgba(0, 0, 0, 0.8)",
          backdropFilter: "blur(10px)",
          transition: "all 0.3s ease",
          boxShadow: scrolled ? "0 2px 20px rgba(0, 0, 0, 0.5)" : "none",
          padding: scrolled ? "0.5rem 0" : "1rem 0",
        }}
      >
        <Container fluid> {/* Cambiado a Container fluid para más espacio */}
          {/* Logo */}
          <Link to="/" className="navbar-brand d-flex align-items-center">
            <img
              src="/assets/images/navbar-logo.png"
              alt="CEC Logo"
              height="34"
              style={{ transition: "transform 0.3s ease" }}
              onMouseOver={(e) =>
                (e.currentTarget.style.transform = "scale(1.05)")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.transform = "scale(1)")
              }
            />
            <img
              src="/assets/images/cec-text.png"
              alt="CEC 2026"
              height="28"
              className="ms-2"
            />
          </Link>

          {/* Botón móvil */}
          <NavbarToggler onClick={toggle} className="border-0">
            <span
              className="navbar-toggler-icon"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba%28255, 255, 255, 0.8%29' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e")`,
              }}
            ></span>
          </NavbarToggler>

          {/* Menú colapsable */}
          <Collapse
            id="navbarCollapse"
            isOpen={isOpenMenu}
            navbar
            className="justify-content-between"
          >
            <Nav className="navbar-nav mx-auto" id="navbar-navlist">
              {/* Inicio */}
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/"
                  className={`nav-link-custom ${isActive("/")}`}
                  onClick={() => {
                    if (window.location.pathname === "/") {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                    setIsOpenMenu(false);
                  }}
                >
                  {t("Inicio")}
                </NavLink>
              </NavItem>

              {/* Congreso */}
              <UncontrolledDropdown nav inNavbar>
                <DropdownToggle
                  nav
                  caret
                  className="nav-link-custom dropdown-custom"
                >
                  {t("Congreso")}
                </DropdownToggle>
                <DropdownMenu
                  className="dropdown-menu-dark"
                  style={{
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <DropdownItem>
                    <Link to="/previous-editions" className="dropdown-item text-white">
                      {t("Ediciones Anteriores")}
                    </Link>
                  </DropdownItem>
                  <DropdownItem>
                    <Link to="/speakers" className="dropdown-item text-white">
                      {t("Conferencistas")}
                    </Link>
                  </DropdownItem>
                  <DropdownItem>
                    <Link to="/program" className="dropdown-item text-white">
                      {t("Programa")}
                    </Link>
                  </DropdownItem>
                  <DropdownItem>
                    <Link to="/hotels" className="dropdown-item text-white">
                      {t("Alojamiento")}
                    </Link>
                  </DropdownItem>
                  <DropdownItem>
                    <Link to="/handbook" className="dropdown-item text-white">
                      {t("Guía del Participante")}
                    </Link>
                  </DropdownItem>
                  <DropdownItem divider style={{ borderColor: "rgba(255,255,255,0.15)" }} />
                  <DropdownItem>
                    <Link to="/about" className="dropdown-item text-white">
                      {t("Comité Organizador")}
                    </Link>
                  </DropdownItem>
                  <DropdownItem>
                    <a
                      href="#sponsors"
                      className="dropdown-item text-white"
                      onClick={(e) => {
                        if (window.location.pathname === "/") {
                          e.preventDefault();
                          scrollToSection("sponsors");
                        } else {
                          window.location.href = "/#sponsors";
                        }
                      }}
                    >
                      {t("Instituciones Organizadoras")}
                    </a>
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>

              {/* Ponencias */}
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/call-for-abstracts"
                  className={`nav-link-custom ${isActive("/call-for-abstracts")}`}
                  onClick={() => setIsOpenMenu(false)}
                >
                  {t("Ponencias")}
                </NavLink>
              </NavItem>

              {/* Patrocinadores */}
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/sponsors"
                  className={`nav-link-custom ${isActive("/sponsors")}`}
                  onClick={() => setIsOpenMenu(false)}
                >
                  {t("Patrocinadores")}
                </NavLink>
              </NavItem>

              {/* Contacto */}
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/contact"
                  className={`nav-link-custom ${isActive("/contact")}`}
                  onClick={() => setIsOpenMenu(false)}
                >
                  {t("Contacto")}
                </NavLink>
              </NavItem>
            </Nav>

            {/* Botón de registro + idioma */}
            <div className="d-flex align-items-center gap-2">
              <Link
                to="/registration"
                className="btn btn-sm px-3 py-2"
                onClick={() => setIsOpenMenu(false)}
                style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  fontWeight: "700",
                  fontSize: "0.85rem",
                  borderRadius: "8px",
                  border: "none",
                  boxShadow: "0 3px 10px rgba(102, 126, 234, 0.4)",
                  transition: "all 0.3s ease",
                  whiteSpace: "nowrap",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 5px 15px rgba(102, 126, 234, 0.5)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 3px 10px rgba(102, 126, 234, 0.4)";
                }}
              >
                🎟️ {t("Regístrate")}
              </Link>
              <LanguageButton />
            </div>
          </Collapse>
        </Container>
      </nav>

      {/* Estilos adicionales optimizados */}
      <style jsx="true">{`
  .navbar-custom {
    transition: all 0.3s ease;
  }

  /* Estilos consistentes para todos los enlaces */
  .nav-link-custom {
    font-size: 0.9rem !important; /* Tamaño de fuente más pequeño y consistente */
    font-weight: 500;
    letter-spacing: 0.3px;
    padding: 0.5rem 0.8rem !important; /* Padding reducido para que quepan todos */
    transition: all 0.3s ease;
    background-color: transparent !important;
    border: none !important;
    color: #ffffff !important;
    white-space: nowrap; /* Evita que el texto se rompa en múltiples líneas */
  }

  /* Estilos para dropdowns */
  .dropdown-custom {
    font-size: 0.9rem !important;
  }

  /* Sin cambios de color en hover o active */
  .nav-link-custom:hover,
  .nav-link-custom.active {
    color: #ffffff !important;
    text-decoration: underline; /* opcional: leve feedback visual */
  }

  .dropdown-toggle::after {
    color: #ffffff;
    margin-left: 0.3rem;
  }

  .dropdown-menu-dark .dropdown-item {
    color: #ffffff;
    font-size: 0.9rem;
    transition: all 0.3s ease;
  }

  .dropdown-menu-dark .dropdown-item:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: #ffffff !important;
  }

  .navbar-toggler:focus {
    box-shadow: none;
  }

  /* Asegurar que el nav ocupe el espacio disponible */
  .navbar-nav {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem; /* Espacio entre items */
  }

  /* Para pantallas más pequeñas pero aún desktop */
  @media (min-width: 992px) and (max-width: 1200px) {
    .nav-link-custom {
      font-size: 0.85rem !important;
      padding: 0.5rem 0.6rem !important;
    }
    
    .navbar-brand img {
      height: 30px;
    }
  }

  /* Para pantallas grandes */
  @media (min-width: 1200px) {
    .container-fluid {
      max-width: 1400px; /* Limitar el ancho máximo */
      margin: 0 auto;
    }
  }

  /* Para móviles */
  @media (max-width: 991px) {
    .navbar-nav {
      flex-direction: column;
      padding: 1rem 0;
      background-color: rgba(0, 0, 0, 0.95);
      border-radius: 10px;
      margin-top: 1rem;
      gap: 0;
    }

    .nav-link-custom {
      padding: 0.75rem 1rem !important;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      width: 100%;
      font-size: 1rem !important;
    }

    .nav-link-custom:last-child {
      border-bottom: none;
    }

    .nav-link-custom.active {
      border-left: 3px solid rgba(255, 255, 255, 0.2); /* Solo un sutil indicador lateral */
    }
  }
`}</style>

    </>
  );
};